import { useLocation, useMatch } from "react-router-dom";
import {
  SETTINGS_KPI_ARCHIVED_ROUTE,
  SETTINGS_KPI_CREATE_ROUTE,
  SETTINGS_KPI_LIBRARY_ROUTE,
  SETTINGS_KPI_ROUTE,
} from "../../routes.js";

/** Which of the four KPI settings URLs the location is on, plus the KPI it names. */
export interface KpiSettingsRoute {
  /** `/settings/kpi/create`. */
  readonly createMode: boolean;
  /** `/settings/kpi/:kpiId/edit`. */
  readonly editMode: boolean;
  /**
   * Which collection view a static child URL names: `/settings/kpi` is the
   * whole collection, `/library` the templates, `/archived` the retired KPIs
   * (PB-5134, d3). Static segments, so — exactly like `create` — they are
   * excluded from the `:kpiId` reading below and never fetch a KPI.
   */
  readonly section: "all" | "library" | "archived";
  /**
   * The DECODED KPI id from either the detail or the edit URL; `null` on browse
   * and on create. Never the raw path segment — see the encoding note below.
   */
  readonly kpiId: string | null;
}

/**
 * PB-2941 — the ONE reading of the KPI settings URL, shared by `KpiSettings`
 * (which turns it into `KpiManager` props) and `SettingsPage` (which turns it
 * into the third breadcrumb). Both used to parse it independently, with the same
 * three subtleties restated in each; a change to the route shape had to be made
 * twice, and a miss would show as a breadcrumb that disagreed with the page
 * beside it.
 *
 * The three subtleties, stated once:
 *
 * - **`useMatch`, not `useParams` or a pathname split.** Callers here are the
 *   PARENT route's element, and `useParams` returns the params of the match its
 *   own RouteContext ends at — the child `:kpiId` match only exists once
 *   `<Outlet/>` renders. `useMatch` tests the location directly.
 * - **`useMatch` DECODES the segment**, undoing the `encodeURIComponent` that
 *   `settingsKpiDetailRoute` applied. A raw pathname split would yield the
 *   encoded form, the API client would encode it a second time, and the read
 *   would miss the detail page's cache entry instead of sharing it.
 * - **`useMatch` is EXACT, not a prefix test.** `/settings/kpi/:kpiId/edit` does
 *   not match the detail pattern, so the edit URL needs its own match — and
 *   `/settings/kpi/create` DOES match the detail pattern (a path segment is a
 *   path segment), so create is excluded explicitly rather than by relying on
 *   React Router's static-over-dynamic ranking, which governs the ROUTE TREE and
 *   not this test.
 */
export function useKpiSettingsRoute(): KpiSettingsRoute {
  const location = useLocation();
  const path = location.pathname.replace(/\/+$/, "");
  const createMode = path === SETTINGS_KPI_CREATE_ROUTE;
  const section: KpiSettingsRoute["section"] =
    path === SETTINGS_KPI_LIBRARY_ROUTE
      ? "library"
      : path === SETTINGS_KPI_ARCHIVED_ROUTE
        ? "archived"
        : "all";
  const detailMatch = useMatch(`${SETTINGS_KPI_ROUTE}/:kpiId`);
  const editMatch = useMatch(`${SETTINGS_KPI_ROUTE}/:kpiId/edit`);
  const kpiId =
    createMode || section !== "all"
      ? null
      : (editMatch?.params.kpiId ?? detailMatch?.params.kpiId ?? null);
  return { createMode, editMode: editMatch !== null, section, kpiId };
}
